import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TrainingModuleCard } from '../../components/training/TrainingModuleCard';
import { trainingService, type Training } from '../../services/training.service';
import { useConfirm } from '../../contexts/ConfirmContext';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type Notice = {
  tone: 'success' | 'error';
  message: string;
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function NoticeBanner({ notice }: { notice: Notice }) {
  const toneMap = {
    success: 'border-success/30 bg-success/10 text-success',
    error: 'border-error/30 bg-error/10 text-error',
  } as const;

  return (
    <div className={`rounded-xl border px-4 py-3 text-sm font-medium ${toneMap[notice.tone]}`}>
      {notice.message}
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, tone, sub }: {
  label: string; value: string | number; icon: string;
  tone: 'primary' | 'success' | 'caution' | 'neutral';
  sub?: string;
}) {
  const toneMap = {
    primary: { icon: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30', glow: 'shadow-primary/5', bar: 'bg-primary' },
    success: { icon: 'text-success', bg: 'bg-success/15', border: 'border-success/30', glow: 'shadow-success/5', bar: 'bg-success' },
    caution: { icon: 'text-caution', bg: 'bg-caution/15', border: 'border-caution/30', glow: 'shadow-caution/5', bar: 'bg-caution' },
    neutral: { icon: 'text-on-surface-variant', bg: 'bg-surface-container', border: 'border-outline-variant/20', glow: 'shadow-none', bar: 'bg-on-surface-variant' },
  };
  const t = toneMap[tone];
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${t.border} bg-surface-container-low p-6 shadow-sm ${t.glow} transition-all hover:-translate-y-0.5 hover:shadow-md`}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${t.bar}`} />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] text-on-surface-variant font-semibold uppercase tracking-wider mb-1">{label}</p>
          <p className="text-3xl font-extrabold text-on-surface tracking-tight">{value}</p>
          {sub && <p className="text-[11px] text-on-surface-variant mt-1.5 font-medium">{sub}</p>}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${t.bg} shadow-sm`}>
          <span className={`material-symbols-outlined block leading-none ${t.icon} text-2xl`}>{icon}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sortable Module Card ───────────────────────────────────────────────────

function SortableModuleCard({
  training,
  onEdit,
  onDelete,
  onNavigate,
}: {
  training: Training;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: training.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.6 : undefined,
  };

  const hasProcessing = training.lessons?.some((l) => (l as { status?: string }).status === 'processing');
  const hasFailed = training.lessons?.some((l) => (l as { status?: string }).status === 'failed');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'rounded-2xl bg-surface-container-high shadow-md' : ''}
    >
      <TrainingModuleCard
        training={training}
        onOpen={() => onNavigate(training.id)}
        showOrder
        thumbnailLeftSlot={
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="cursor-grab rounded-lg bg-black/30 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing"
          >
            <span className="material-symbols-outlined text-lg">drag_indicator</span>
          </button>
        }
        thumbnailRightSlot={
          <>
            {hasProcessing ? <span className="material-symbols-outlined animate-spin text-lg text-tertiary" style={{ animationDuration: '2s' }}>sync</span> : null}
            {hasFailed ? <span className="material-symbols-outlined text-lg text-error" title="Some videos failed processing">error</span> : null}
          </>
        }
        actionsSlot={
          <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(training.id); }}
              className="rounded-lg p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container hover:text-primary"
              title="Edit"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(training.id); }}
              className="rounded-lg p-1.5 text-on-surface-variant transition-colors hover:bg-error/10 hover:text-error"
              title="Delete"
            >
              <span className="material-symbols-outlined text-[18px]">delete</span>
            </button>
          </div>
        }
      />
    </div>
  );
}

// ─── Thumbnail Upload Area ──────────────────────────────────────────────────

function ThumbnailUpload({
  previewUrl,
  onFileSelect,
  uploading,
}: {
  previewUrl?: string | null;
  onFileSelect: (file: File) => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      onClick={() => inputRef.current?.click()}
      className="relative w-full h-36 rounded-xl border-2 border-dashed border-outline-variant/30 hover:border-primary/40 bg-surface-container overflow-hidden cursor-pointer transition-colors group"
    >
      {previewUrl ? (
        <img src={previewUrl} alt="Thumbnail preview" className="w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <span className="material-symbols-outlined text-3xl text-on-surface-variant/40">image</span>
          <span className="text-xs text-on-surface-variant/60 font-medium">Click to upload thumbnail</span>
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <span className={`material-symbols-outlined text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity ${uploading ? 'animate-spin' : ''}`}>
          {uploading ? 'sync' : 'edit'}
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelect(file);
          if (inputRef.current) inputRef.current.value = '';
        }}
        className="hidden"
      />
    </div>
  );
}

// ─── Create/Edit Modal ──────────────────────────────────────────────────────

type ModalStep = 'form' | 'thumbnail';

function TrainingFormModal({
  open,
  onClose,
  onNotify,
  initial,
  title,
  trainingId,
  thumbnailPresignedUrl,
}: {
  open: boolean;
  onClose: () => void;
  onNotify: (notice: Notice) => void;
  initial?: { title: string; description: string; thumbnailUrl: string };
  title: string;
  trainingId?: string;
  thumbnailPresignedUrl?: string | null;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ModalStep>('form');
  const [form, setForm] = useState(initial ?? { title: '', description: '', thumbnailUrl: '' });
  const [previewUrl, setPreviewUrl] = useState<string | null>(thumbnailPresignedUrl || initial?.thumbnailUrl || null);
  const [pendingThumbnailFile, setPendingThumbnailFile] = useState<File | null>(null);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(trainingId || null);
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    if (open) {
      setStep('form');
      setForm(initial ?? { title: '', description: '', thumbnailUrl: '' });
      setPreviewUrl(thumbnailPresignedUrl || initial?.thumbnailUrl || null);
      setPendingThumbnailFile(null);
      setCreatedId(trainingId || null);
      setNotice(null);
    }
  }, [open, initial?.title, initial?.description, initial?.thumbnailUrl, trainingId, thumbnailPresignedUrl]);

  const createMutation = useMutation({
    mutationFn: (data: { title: string; description?: string }) => trainingService.adminCreate(data),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-trainings'] });
      setCreatedId(data.id);
      if (pendingThumbnailFile) {
        await handleThumbFile(pendingThumbnailFile, data.id);
        onNotify({ tone: 'success', message: 'Module created successfully with thumbnail.' });
        onClose();
        return;
      }
      setStep('thumbnail');
      setNotice({ tone: 'success', message: 'Module created. You can upload a thumbnail now or skip for later.' });
    },
    onError: (error) => {
      setNotice({ tone: 'error', message: getErrorMessage(error) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & { title: string; description?: string }) =>
      trainingService.adminUpdate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-trainings'] });
      onNotify({ tone: 'success', message: 'Module updated successfully.' });
      onClose();
    },
    onError: (error) => {
      setNotice({ tone: 'error', message: getErrorMessage(error) });
    },
  });

  if (!open) return null;

  async function handleThumbFile(file: File, explicitTargetId?: string) {
    const targetId = explicitTargetId ?? createdId;
    if (!targetId) return;
    setUploadingThumb(true);
    setNotice(null);
    try {
      const { uploadUrl, s3Key } = await trainingService.adminPresignThumbnail(targetId, file.name, file.type);
      const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!res.ok) throw new Error('Upload failed');
      const { thumbnailUrl } = await trainingService.adminConfirmThumbnail(targetId, s3Key);
      setPreviewUrl(thumbnailUrl);
      setForm((f) => ({ ...f, thumbnailUrl: s3Key }));
      setPendingThumbnailFile(null);
      await queryClient.refetchQueries({ queryKey: ['admin-trainings'] });
      setNotice({ tone: 'success', message: 'Thumbnail updated successfully.' });
    } catch (err) {
      setNotice({ tone: 'error', message: `Thumbnail upload failed: ${getErrorMessage(err)}` });
    } finally {
      setUploadingThumb(false);
    }
  }

  function handleFormSubmit() {
    if (!form.title.trim()) return;
    setNotice(null);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
    };
    if (trainingId) {
      updateMutation.mutate({ id: trainingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-surface-container-low rounded-2xl border border-outline-variant/15 p-6 shadow-xl space-y-4">
        <h2 className="text-lg font-bold text-on-surface">{title}</h2>
        {notice ? <NoticeBanner notice={notice} /> : null}

        {step === 'form' ? (
          <>
            <div className="space-y-2">
              <div>
                <p className="text-xs font-medium text-on-surface-variant">Thumbnail</p>
                <p className="mt-0.5 text-[11px] text-on-surface-variant/60">
                  {createdId
                    ? 'Click the image area to upload or replace the module cover.'
                    : 'Choose a thumbnail now and it will upload automatically after the module is created.'}
                </p>
              </div>
              <ThumbnailUpload
                previewUrl={previewUrl}
                onFileSelect={(file) => {
                  if (createdId) {
                    void handleThumbFile(file);
                    return;
                  }
                  setPendingThumbnailFile(file);
                  setPreviewUrl(URL.createObjectURL(file));
                }}
                uploading={uploadingThumb}
              />
            </div>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Module title"
              className="w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
              required
            />
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Description (optional)"
              className="w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors resize-none"
              rows={3}
            />
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleFormSubmit}
                disabled={!form.title.trim() || isPending}
                className="flex-1 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isPending ? 'Saving...' : trainingId ? 'Save Module' : 'Create Module'}
              </button>
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl bg-surface-container text-sm font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-on-surface-variant">Module created! Add a thumbnail to make it stand out.</p>
            <ThumbnailUpload previewUrl={previewUrl} onFileSelect={handleThumbFile} uploading={uploadingThumb} />
            <div className="flex gap-2 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all"
              >
                {previewUrl ? 'Done' : 'Skip for now'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function AdminTrainings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pageNotice, setPageNotice] = useState<Notice | null>(null);

  const { data: rawTrainings = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-trainings'],
    queryFn: () => trainingService.adminList(),
  });

  // Local state for drag-and-drop ordering (optimistic)
  const [trainings, setTrainings] = useState<Training[]>([]);
  useEffect(() => {
    setTrainings(rawTrainings);
  }, [rawTrainings]);

  const stats = {
    total: trainings.length,
    lessons: trainings.reduce((sum, t) => sum + (t._count?.lessons ?? 0), 0),
    processing: trainings.reduce((sum, t) => sum + (t.lessons?.filter((l) => (l as { status?: string }).status === 'processing').length ?? 0), 0),
    withQuiz: trainings.reduce((sum, t) => sum + (t.lessons?.filter((l) => {
      const quiz = (l as { quiz?: unknown[] }).quiz;
      return quiz && Array.isArray(quiz) && quiz.length > 0;
    }).length ?? 0), 0),
  };

  const reorderMutation = useMutation({
    mutationFn: trainingService.adminReorderTrainings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-trainings'] });
      setPageNotice({ tone: 'success', message: 'Module order updated.' });
    },
    onError: (error) => {
      setPageNotice({ tone: 'error', message: `Could not reorder modules: ${getErrorMessage(error)}` });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: trainingService.adminDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-trainings'] });
      setPageNotice({ tone: 'success', message: 'Module deleted successfully.' });
    },
    onError: (error) => {
      setPageNotice({ tone: 'error', message: `Could not delete module: ${getErrorMessage(error)}` });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = trainings.findIndex((t) => t.id === active.id);
    const newIndex = trainings.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = trainings;
    const next = arrayMove(trainings, oldIndex, newIndex);
    setTrainings(next);

    // Persist new order (0-based sequence)
    const orders = next.map((t, i) => ({ id: t.id, order: i }));
    reorderMutation.mutate(orders, {
      onError: () => {
        setTrainings(previous);
      },
    });
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8">
        <div className="h-8 w-56 bg-surface-container-high rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-surface-container-high rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-48 bg-surface-container-high rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-1">Content Management</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-on-surface">Video Modules</h1>
        </div>
        <NoticeBanner notice={{ tone: 'error', message: `Could not load modules: ${getErrorMessage(error)}` }} />
        <div>
          <button
            onClick={() => refetch()}
            className="px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8 md:space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-1">Content Management</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-on-surface">Video Modules</h1>
          <p className="text-on-surface-variant text-sm mt-1">{stats.total} modules · {stats.lessons} lessons</p>
        </div>
        <button
          onClick={() => {
            setPageNotice(null);
            setCreateOpen(true);
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all self-start sm:self-auto"
        >
          <span className="material-symbols-outlined text-lg">add</span>
          Create Module
        </button>
      </div>

      {pageNotice ? <NoticeBanner notice={pageNotice} /> : null}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Modules" value={stats.total} icon="school" tone="primary" sub="Training modules" />
        <StatCard label="Lessons" value={stats.lessons} icon="play_circle" tone="success" sub="Total videos" />
        <StatCard label="Processing" value={stats.processing} icon="sync" tone="caution" sub="AI in progress" />
        <StatCard label="With Quiz" value={stats.withQuiz} icon="quiz" tone="neutral" sub="Active quizzes" />
      </div>

      {/* Module Grid */}
      {trainings.length === 0 ? (
        <div className="text-center py-20 bg-surface-container-low rounded-2xl border border-outline-variant/10">
          <span className="material-symbols-outlined text-6xl text-on-surface-variant/30 block mb-4">school</span>
          <p className="text-on-surface font-semibold text-lg">No modules yet</p>
          <p className="text-on-surface-variant text-sm mt-1">Create your first training module to get started.</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={trainings.map((t) => t.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trainings.map((training) => (
                <SortableModuleCard
                  key={training.id}
                  training={training}
                  onEdit={(id) => {
                    setPageNotice(null);
                    setEditingId(id);
                  }}
                  onDelete={async (id) => {
                    setPageNotice(null);
                    if (
                      await confirm({
                        title: 'Delete this module?',
                        message: 'This module and all of its lessons will be permanently removed. This cannot be undone.',
                        confirmLabel: 'Delete',
                        danger: true,
                      })
                    ) {
                      deleteMutation.mutate(id);
                    }
                  }}
                  onNavigate={(id) => navigate(`/admin/trainings/${id}`)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <TrainingFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onNotify={setPageNotice}
        title="Create Module"
      />

      {(() => {
        const training = trainings.find((t) => t.id === editingId);
        if (!training) return null;
        return (
          <TrainingFormModal
            open={!!editingId}
            onClose={() => setEditingId(null)}
            onNotify={setPageNotice}
            initial={{
              title: training.title,
              description: training.description || '',
              thumbnailUrl: training.thumbnailUrl || '',
            }}
            title="Edit Module"
            trainingId={training.id}
            thumbnailPresignedUrl={training.thumbnailPresignedUrl}
          />
        );
      })()}
    </div>
  );
}
